/**
 * Утилиты для работы с Telegram Web App API
 */

/**
 * Проверяет, доступен ли Telegram Web App API
 */
export function isTelegramWebAppAvailable() {
    return !!(window.Telegram && window.Telegram.WebApp);
}

/**
 * Получает информацию о пользователе из Telegram Web App
 */
export function getTelegramUser() {
    if (isTelegramWebAppAvailable()) {
        return window.Telegram.WebApp.initDataUnsafe?.user || null;
    }
    return null;
}

/**
 * Показывает всплывающее окно в Telegram Web App
 */
export function showTelegramPopup(options) {
    if (isTelegramWebAppAvailable() && window.Telegram.WebApp.showPopup) {
        window.Telegram.WebApp.showPopup(options);
    }
}

/**
 * Запрашивает медиафайлы через Telegram Web App API
 */
export async function requestTelegramMedia(options = {}) {
    if (!isTelegramWebAppAvailable()) {
        throw new Error('Telegram Web App API недоступен');
    }

    if (!window.Telegram.WebApp.requestMedia) {
        throw new Error('Метод requestMedia недоступен в данной версии Telegram');
    }

    const defaultOptions = {
        type: 'photo',
        multiple: true,
        maxCount: 10
    };

    return await window.Telegram.WebApp.requestMedia({
        ...defaultOptions,
        ...options
    });
}

/**
 * Конвертирует файлы из Telegram в File объекты
 */
export async function convertTelegramFilesToFileObjects(telegramFiles) {
    const files = [];
    
    for (const telegramFile of telegramFiles) {
        try {
            // Получаем файл по URL
            const response = await fetch(telegramFile.url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blob = await response.blob();
            
            // Определяем MIME тип
            let mimeType = blob.type;
            if (!mimeType || mimeType === 'application/octet-stream') {
                // Пытаемся определить тип по расширению файла
                const fileName = telegramFile.file_name || '';
                const extension = fileName.split('.').pop()?.toLowerCase();
                
                switch (extension) {
                    case 'jpg':
                    case 'jpeg':
                    case 'jfif':
                        mimeType = 'image/jpeg';
                        break;
                    case 'png':
                        mimeType = 'image/png';
                        break;
                    case 'gif':
                        mimeType = 'image/gif';
                        break;
                    case 'mp4':
                        mimeType = 'video/mp4';
                        break;
                    case 'avi':
                        mimeType = 'video/x-msvideo';
                        break;
                    default:
                        mimeType = 'image/jpeg'; // fallback
                }
            }
            
            // Создаем File объект
            const file = new File([blob], telegramFile.file_name || `file_${Date.now()}.jpg`, {
                type: mimeType,
                lastModified: Date.now()
            });
            
            files.push(file);
        } catch (error) {
            console.error('Ошибка конвертации файла:', error);
            // Продолжаем обработку других файлов
        }
    }
    
    return files;
}

/**
 * Инициализирует Telegram Web App
 */
export function initTelegramWebApp() {
    if (isTelegramWebAppAvailable()) {
        // Настраиваем тему
        window.Telegram.WebApp.setHeaderColor('#0088cc');
        window.Telegram.WebApp.setBackgroundColor('#ffffff');
        
        // Расширяем на всю высоту
        window.Telegram.WebApp.expand();
        
        // Включаем закрытие по свайпу
        window.Telegram.WebApp.enableClosingConfirmation();
        
        return true;
    }
    return false;
}

/**
 * Получает данные инициализации Telegram Web App
 */
export function getTelegramInitData() {
    if (isTelegramWebAppAvailable()) {
        return window.Telegram.WebApp.initData || '';
    }
    return '';
}

/**
 * Проверяет, запущено ли приложение в Telegram
 */
export function isRunningInTelegram() {
    return isTelegramWebAppAvailable() && 
           window.Telegram.WebApp.initDataUnsafe && 
           Object.keys(window.Telegram.WebApp.initDataUnsafe).length > 0;
} 

/**
 * Закрывает приложение в Telegram
 */
export function closeTelegramWebApp() {
    if (isTelegramWebAppAvailable()) {
        window.Telegram.WebApp.close();        
    }
}
